import { newSelectionPayload } from './onboarding';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { client } from '@frontend/features/common/services';
import { HotelLocationPicker } from '@frontend/features/hotels/HotelLocationPicker';
import type { HotelLocation } from '@frontend/features/hotels/searchModel';
import { catalogAdminKey } from './queries';
export interface Candidate {
  id: string;
  label: string;
  country_code: string | null;
  locality: string | null;
}
export function CandidatePicker({
  kind,
  value,
  onChange,
  showIds = true,
  label,
  searchLabel,
}: {
  kind: 'geo' | 'hotel';
  showIds?: boolean;
  label?: string;
  searchLabel?: string;
  value: Candidate | null;
  onChange(value: Candidate | null): void;
}) {
  const { t } = useTranslation();
  const [input, setInput] = useState(''),
    [q, setQ] = useState(''),
    [offset, setOffset] = useState(0);
  const results = useQuery(
    [...catalogAdminKey, 'candidates', kind, q, offset],
    async () =>
      (
        await client.get<{ data: Candidate[]; meta: { has_more: boolean } }>(`hotel-catalog-admin/candidates/${kind}`, {
          params: { q, offset },
        })
      ).data,
    { enabled: q.length >= 2, retry: false },
  );
  return (
    <Stack spacing={1}>
      <TextField
        label={label ?? t(`hotelOnboarding.${kind}Candidate`)}
        value={input}
        inputProps={{ maxLength: 128 }}
        onChange={(e) => {
          setInput(e.target.value);
          onChange(null);
        }}
      />
      <Button
        disabled={input.trim().length < 2 || results.isFetching}
        onClick={() => {
          if (q === input.trim() && offset === 0) results.refetch();
          setQ(input.trim());
          setOffset(0);
          onChange(null);
        }}
      >
        {searchLabel ?? t('hotelOnboarding.find')}
      </Button>
      {results.isError && <Alert severity="error">{t('hotelOnboarding.loadError')}</Alert>}
      {(results.data?.data ?? []).map((row) => (
        <Button
          disabled={results.isFetching || results.isError}
          key={row.id}
          variant={value?.id === row.id ? 'contained' : 'outlined'}
          onClick={() => onChange(row)}
        >
          <Stack alignItems="flex-start" sx={{ minWidth: 0, textAlign: 'left', overflowWrap: 'anywhere' }}>
            <Typography variant="body2">
              {row.label} · {row.country_code} {row.locality}
            </Typography>
            {showIds && (
              <Typography variant="caption">{t('hotelDestinationMembership.reference', { id: row.id })}</Typography>
            )}
          </Stack>
        </Button>
      ))}
      {q && results.data?.data.length === 0 && <Typography>{t('hotelCatalogAdmin.empty')}</Typography>}
      <Stack direction="row">
        <Button disabled={!offset || results.isFetching} onClick={() => setOffset((v) => Math.max(0, v - 25))}>
          {t('hotelCatalogAdmin.previous')}
        </Button>
        <Button
          disabled={results.isError || !results.data?.meta.has_more || results.isFetching || offset >= 10000}
          onClick={() => setOffset((v) => v + 25)}
        >
          {t('hotelCatalogAdmin.next')}
        </Button>
      </Stack>
    </Stack>
  );
}
export function AddSelection() {
  const { t } = useTranslation();
  const cache = useQueryClient();
  const [kind, setKind] = useState<'place-mappings' | 'hotel-memberships' | null>(null);
  const [geo, setGeo] = useState<Candidate | null>(null),
    [hotel, setHotel] = useState<Candidate | null>(null),
    [place, setPlace] = useState<HotelLocation | null>(null);
  const [reason, setReason] = useState(''),
    [rank, setRank] = useState('0'),
    [enabled, setEnabled] = useState(false),
    [conflict, setConflict] = useState(false),
    [resolving, setResolving] = useState(false);
  const save = useMutation(
    async () => {
      if (!kind || !geo || !reason.trim() || conflict) throw new Error('selection');
      await client.put(
        `hotel-catalog-admin/${kind}`,
        newSelectionPayload(
          kind,
          geo.id,
          kind === 'place-mappings' ? (place?.type === 'destination' ? place.placeId : '') : (hotel?.id ?? ''),
          enabled,
          reason,
          Number(rank),
        ),
      );
    },
    {
      onSuccess: () => {
        setKind(null);
        cache.invalidateQueries(catalogAdminKey);
      },
      onError: (e: any) => {
        if (e?.response?.status === 409) setConflict(true);
      },
    },
  );
  const open = (next: typeof kind) => {
    setKind(next);
    setGeo(null);
    setHotel(null);
    setPlace(null);
    setReason('');
    setRank('0');
    setEnabled(false);
    setConflict(false);
    save.reset();
  };
  return (
    <>
      <Stack direction="row" spacing={2}>
        <Button onClick={() => open('place-mappings')}>{t('hotelOnboarding.addDestination')}</Button>
        <Button onClick={() => open('hotel-memberships')}>{t('hotelOnboarding.addHotel')}</Button>
      </Stack>
      <Dialog
        open={!!kind}
        fullWidth
        maxWidth="md"
        onClose={() => {
          if (!save.isLoading) setKind(null);
        }}
      >
        <DialogTitle>
          {t(kind === 'place-mappings' ? 'hotelOnboarding.addDestination' : 'hotelOnboarding.addHotel')}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1, pointerEvents: save.isLoading || conflict ? 'none' : undefined }}>
            <Alert severity="info">{t('hotelOnboarding.selectionNotice')}</Alert>
            <CandidatePicker kind="geo" value={geo} onChange={setGeo} />
            {kind === 'place-mappings' ? (
              <>
                <HotelLocationPicker value={place} onChange={setPlace} onResolving={setResolving} />
                {place?.type === 'point' && <Alert severity="warning">{t('hotelOnboarding.destinationOnly')}</Alert>}
              </>
            ) : (
              <>
                <CandidatePicker kind="hotel" value={hotel} onChange={setHotel} />
                <TextField
                  label={t('hotelCatalogAdmin.rank')}
                  type="number"
                  value={rank}
                  onChange={(e) => setRank(e.target.value)}
                  inputProps={{ min: 0, max: 2147483647 }}
                />
              </>
            )}
            <FormControlLabel
              control={<Checkbox checked={enabled} onChange={(_, v) => setEnabled(v)} />}
              label={t('hotelCatalogAdmin.enabled')}
            />
            <TextField
              label={t('hotelCatalogAdmin.reason')}
              value={reason}
              inputProps={{ maxLength: 512 }}
              onChange={(e) => setReason(e.target.value)}
            />
          </Stack>
          {save.isError && (
            <Alert severity="error">{t(conflict ? 'hotelCatalogAdmin.conflict' : 'hotelCatalogAdmin.saveError')}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={save.isLoading} onClick={() => setKind(null)}>
            {t('hotelCatalogAdmin.cancel')}
          </Button>
          {conflict ? (
            <Button
              onClick={() => {
                setKind(null);
                cache.invalidateQueries(catalogAdminKey);
              }}
            >
              {t('hotelCatalogAdmin.reload')}
            </Button>
          ) : (
            <Button
              disabled={
                save.isLoading ||
                resolving ||
                !geo ||
                !reason.trim() ||
                (kind === 'place-mappings'
                  ? place?.type !== 'destination'
                  : !hotel ||
                    !rank.trim() ||
                    !Number.isSafeInteger(Number(rank)) ||
                    Number(rank) < 0 ||
                    Number(rank) > 2147483647)
              }
              onClick={() => save.mutate()}
            >
              {t('hotelCatalogAdmin.save')}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
